// Submenús (autenticacion.submenus). Sistemas → Gestión de Menús → SubMenú.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, SELECT_PLACEHOLDER, Switch, auth, fieldCls } from './shared';

interface Submenu { id: string; menu_id: string; nombre: string; descripcion: string | null; url: string | null; orden: number; activo: boolean; menus?: { nombre: string } | null }
interface MenuOpt { id: string; nombre: string }
const vacio = { menu_id: '', nombre: '', descripcion: '', url: '', orden: 0, activo: true };

export function Submenus() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Submenu[]>({ queryKey: ['adm-submenus'], queryFn: () => auth.list<Submenu>('submenus', '?select=*,menus(nombre)&order=orden') });
  const { data: menus = [] } = useQuery<MenuOpt[]>({ queryKey: ['adm-menus-opt'], queryFn: () => auth.list<MenuOpt>('menus', '?select=id,nombre&order=nombre') });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const [fEstado, setFEstado] = useState('todos');
  const [fMenu, setFMenu] = useState('');
  const filtrados = useMemo(() => rows.filter((s) =>
    (!fNombre.trim() || s.nombre.toLowerCase().includes(fNombre.trim().toLowerCase())) &&
    (fEstado === 'todos' || (fEstado === 'activo' ? s.activo : !s.activo)) &&
    (!fMenu || s.menu_id === fMenu)
  ), [rows, fNombre, fEstado, fMenu]);

  const abrir = (s?: Submenu) => {
    if (s) { setEditId(s.id); setForm({ menu_id: s.menu_id, nombre: s.nombre, descripcion: s.descripcion ?? '', url: s.url ?? '', orden: s.orden, activo: s.activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { menu_id: form.menu_id, nombre: form.nombre, descripcion: form.descripcion || null, url: form.url || null, orden: form.orden, activo: form.activo, updated_at: new Date().toISOString() };
      return editId ? auth.update('submenus', editId, body) : auth.create('submenus', body);
    },
    onSuccess: () => { toast.success('Submenú guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-submenus'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('submenus', id),
    onSuccess: () => { toast.success('Submenú eliminado'); void qc.invalidateQueries({ queryKey: ['adm-submenus'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Submenús</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo submenú</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${fieldCls} w-64`} placeholder="Filtrar por nombre…" value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
        <select className={`${fieldCls} w-40`} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option value="todos">Todos los estados</option><option value="activo">Activos</option><option value="inactivo">Inactivos</option>
        </select>
        <select className={`${fieldCls} w-48`} value={fMenu} onChange={(e) => setFMenu(e.target.value)}>
          <option value="">Todos los menús</option>{menus.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
        </select>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Submenús ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Menú</th><th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin submenús</td></tr>}
              {filtrados.map((s) => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{s.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{s.menus?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono">{s.orden}</td>
                  <td className="py-2 pr-3">{s.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(s)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${s.nombre}"?`, { peligro: true })) eliminar.mutate(s.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar submenú' : 'Nuevo submenú'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Menú *">
              <select className={fieldCls} value={form.menu_id} onChange={(e) => setForm({ ...form, menu_id: e.target.value })}>
                <option value="">{SELECT_PLACEHOLDER}</option>
                {menus.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="URL"><input className={fieldCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || !form.menu_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
